import { useCallback, useState } from "react";

import type { Snack, SnackList } from "@/lib/gql/__generated__/graphql";
import { SnackBlockItemType } from "@/lib/gql/__generated__/graphql";
import { getUUID } from "@/lib/uuid/get-uuid";

import type { ForArrayType, ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

import type { DesignSnackLayoutSettingsFormModelOnSetViewModelType } from "../..";
import type { DesignSnackBlocksFormModel } from "../../type";
import type { SnackBlocksLayoutEnumType, SnackBlocksZodSchemaType } from "../../validation-schema";

type Props = {
	viewModel: DesignSnackBlocksFormModel["ViewModel"];
	onSetViewModel: DesignSnackLayoutSettingsFormModelOnSetViewModelType;
	snackList: SnackList;
};

export type SnackBlocksFormModelContentsMethodsType = {
	selectBlockCountModalState: {
		isShowSelectBlockCountModal: boolean;
		activateSelectBlockCountModal: () => void;
		deactivateSelectBlockCountModal: () => void;
		handleOpenChangeSelectBlockCountModal: (input: boolean) => void;
	};

	blocks: ForArrayType<SnackBlocksZodSchemaType["blocks"][number][]>;
	onSetBlockTitle: ({ title, index }: { title: string; index: number }) => void;
	onSetBlocks: ({ blocksUUIDs }: { blocksUUIDs: SnackBlocksZodSchemaType["blocks"][number]["uuid"][] }) => void;
	onDeleteBlock: ({ targetUUID }: { targetUUID: string }) => void;
	onAddBlock: () => void;

	lastBlock: ForAutoGenerator<SnackBlocksZodSchemaType["blocks"][number], ""> | null;

	onSetBlockLayoutType: ({
		layoutType,
		targetUUID,
	}: {
		layoutType: SnackBlocksLayoutEnumType;
		targetUUID: string;
	}) => void;

	onRemoveSnackFromBlock: ({ blockUUID, snackUUID }: { blockUUID: string; snackUUID: string }) => void;

	selectableSnacks: SelectableSnackItemType[];
	onAddSnackToBlock: ({
		blockUUID,
		selectedSnack,
		snackIndex,
	}: {
		blockUUID: string;
		selectedSnack: SelectableSnackItemType;
		snackIndex: number;
	}) => void;
};

export type SelectableSnackItemType = Pick<Snack, "thumbnail" | "title" | "uuid" | "isPublished">;

export const useSnackSettingsFormModelContentsMethods = ({
	viewModel,
	onSetViewModel,
	snackList,
}: Props): SnackBlocksFormModelContentsMethodsType => {
	const { blocks } = viewModel;

	// Blockの個数を選択するモーダルの表示状態
	const [isShowSelectBlockCountModal, setIsShowSelectBlockCountModal] = useState<boolean>(false);
	const activateSelectBlockCountModal = useCallback(() => {
		setIsShowSelectBlockCountModal(true);
	}, [setIsShowSelectBlockCountModal]);
	const deactivateSelectBlockCountModal = useCallback(() => {
		setIsShowSelectBlockCountModal(false);
	}, [setIsShowSelectBlockCountModal]);
	const handleOpenChangeSelectBlockCountModal = useCallback(
		(input: boolean) => {
			setIsShowSelectBlockCountModal(input);
		},
		[setIsShowSelectBlockCountModal],
	);

	const onSetBlockTitle = useCallback(
		({ title, index }: { title: string; index: number }) => {
			const blocksValue = blocks.value;
			const newBlocksValue = blocksValue.map((block, i) => {
				if (i === index) {
					return {
						...block,
						title: {
							isEdited: true,
							value: title,
						},
					};
				}
				return block;
			});

			onSetViewModel([
				{
					key: "blocks",
					value: {
						isEdited: true,
						hasError: false,
						value: newBlocksValue,
					},
				},
			]);
		},
		[blocks.value, onSetViewModel],
	);

	const onSetBlocks = useCallback(
		({ blocksUUIDs }: { blocksUUIDs: SnackBlocksZodSchemaType["blocks"][number]["uuid"][] }) => {
			// blocks.value を blocksUUIDsの順番に並び替える
			const newBlocksValue = blocks.value.sort((a, b) => {
				const aIndex = blocksUUIDs.indexOf(a.uuid.value);
				const bIndex = blocksUUIDs.indexOf(b.uuid.value);
				return aIndex - bIndex;
			});

			onSetViewModel([
				{
					key: "blocks",
					value: {
						isEdited: true,
						hasError: false,
						value: newBlocksValue,
					},
				},
			]);
		},
		[blocks.value, onSetViewModel],
	);

	const onDeleteBlock = useCallback(
		({ targetUUID }: { targetUUID: string }) => {
			const blocksValue = blocks.value;
			const newBlocksValue = blocksValue.filter(({ uuid }) => uuid.value !== targetUUID);

			onSetViewModel([
				{
					key: "blocks",
					value: {
						isEdited: true,
						hasError: false,
						value: newBlocksValue,
					},
				},
			]);
		},
		[blocks.value, onSetViewModel],
	);

	const onAddBlock = useCallback(() => {
		const blocksValue = blocks.value;
		const newBlocksValue = [
			...blocksValue,
			{
				title: {
					isEdited: false,
					value: "",
				},
				uuid: {
					isEdited: false,
					value: getUUID(),
				},
				layoutType: {
					isEdited: false,
					value: "column-1",
				},
				items: {
					isEdited: false,
					hasError: false,
					value: [],
				},
			},
		] satisfies ForArrayType<SnackBlocksZodSchemaType["blocks"][number][]>["value"];

		onSetViewModel([
			{
				key: "blocks",
				value: {
					isEdited: true,
					hasError: false,
					value: newBlocksValue,
				},
			},
		]);

		activateSelectBlockCountModal();
	}, [activateSelectBlockCountModal, blocks.value, onSetViewModel]);

	// 最後尾のBlock
	const lastBlock = blocks.value.at(-1) ?? null;

	const onSetBlockLayoutType = useCallback(
		({ layoutType, targetUUID }: { layoutType: SnackBlocksLayoutEnumType; targetUUID: string }) => {
			const blocksValue = blocks.value;
			const columns = layoutType === "column-1" ? 1 : layoutType === "column-2" ? 2 : 3;

			const newBlocksValue = blocksValue.map((block) => {
				if (block.uuid.value === targetUUID) {
					return {
						...block,
						items: block.items.value.length
							? {
									isEdited: true,
									hasError: false,
									value:
										columns > block.items.value.length
											? [
													...block.items.value,
													...new Array(columns - block.items.value.length).fill({
														isEmpty: {
															isEdited: true,
															value: true as const,
														},
													}),
												]
											: block.items.value.slice(0, columns),
								}
							: // 初期作成時
								{
									isEdited: true,
									hasError: false,
									value: new Array(columns).fill({
										isEmpty: {
											isEdited: true,
											value: true as const,
										},
									}),
								},
						layoutType: {
							isEdited: true,
							value: layoutType,
						},
					};
				}
				return block;
			});

			onSetViewModel([
				{
					key: "blocks",
					value: {
						isEdited: true,
						hasError: false,
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-expect-error
						value: newBlocksValue,
					},
				},
			]);
		},
		[blocks.value, onSetViewModel],
	);

	// 特定のblockから特定のスナックを取り除く
	const onRemoveSnackFromBlock = useCallback(
		({ blockUUID, snackUUID }: { blockUUID: string; snackUUID: string }) => {
			const blocksValue = blocks.value;
			const newBlocksValue = blocksValue.map((block) => {
				if (block.uuid.value === blockUUID) {
					const newItemsValue = block.items.value.map((item) => {
						if ("itemUUID" in item && item.itemUUID.value !== snackUUID) {
							return item;
						} else {
							return {
								isEmpty: {
									isEdited: true,
									value: true as const,
								},
							};
						}
					});

					return {
						...block,
						items: {
							isEdited: true,
							hasError: false,
							value: newItemsValue,
						},
					};
				}
				return block;
			});

			onSetViewModel([
				{
					key: "blocks",
					value: {
						isEdited: true,
						hasError: false,
						value: newBlocksValue,
					},
				},
			]);
		},
		[blocks.value, onSetViewModel],
	);

	// 選択可能なスナックのリスト
	const selectableSnacks = snackList.snacks.map((snack) => {
		return {
			thumbnail: snack.thumbnail,
			title: snack.title,
			uuid: snack.uuid,
			isPublished: snack.isPublished,
		} satisfies SelectableSnackItemType;
	});

	// snackを特定のblockに追加する
	const onAddSnackToBlock = useCallback(
		({
			blockUUID,
			selectedSnack,
			snackIndex,
		}: {
			blockUUID: string;
			selectedSnack: SelectableSnackItemType;
			snackIndex: number;
		}) => {
			const blocksValue = blocks.value;
			const newBlocksValue = blocksValue.map((block) => {
				if (block.uuid.value === blockUUID) {
					const newItemsValue = block.items.value.map((item, idx) => {
						if ("isEmpty" in item && item.isEmpty.value && snackIndex === idx) {
							// スナックが未選択の場合
							return {
								itemUUID: {
									isEdited: true,
									value: selectedSnack.uuid,
								},
								title: {
									isEdited: true,
									value: selectedSnack.title,
								},
								thumbnail: {
									isEdited: true,
									value: selectedSnack.thumbnail,
								},
								itemType: {
									isEdited: true,
									value: SnackBlockItemType.Snack,
								},
							} satisfies ForAutoGenerator<
								{
									title: string;
									itemType: SnackBlockItemType;
									itemUUID: string;
									thumbnail: string;
								},
								""
							>;
						} else if ("itemUUID" in item && snackIndex === idx) {
							// すでに選択されているスナックを変更されている場合
							return {
								itemUUID: {
									isEdited: true,
									value: selectedSnack.uuid,
								},
								title: {
									isEdited: true,
									value: selectedSnack.title,
								},
								thumbnail: {
									isEdited: true,
									value: selectedSnack.thumbnail,
								},
								itemType: {
									isEdited: true,
									value: SnackBlockItemType.Snack,
								},
							} satisfies ForAutoGenerator<
								{
									title: string;
									itemType: SnackBlockItemType;
									itemUUID: string;
									thumbnail: string;
								},
								""
							>;
						}

						return item;
					});

					return {
						...block,
						items: {
							isEdited: true,
							hasError: false,
							value: newItemsValue,
						},
					};
				}
				return block;
			});

			onSetViewModel([
				{
					key: "blocks",
					value: {
						isEdited: true,
						hasError: false,
						value: newBlocksValue,
					},
				},
			]);
		},
		[blocks.value, onSetViewModel],
	);

	return {
		selectBlockCountModalState: {
			isShowSelectBlockCountModal,
			activateSelectBlockCountModal,
			deactivateSelectBlockCountModal,
			handleOpenChangeSelectBlockCountModal,
		},

		blocks,
		onSetBlockTitle,
		onSetBlocks,
		onDeleteBlock,
		onAddBlock,

		lastBlock,

		onSetBlockLayoutType,

		onRemoveSnackFromBlock,

		selectableSnacks,
		onAddSnackToBlock,
	};
};
